import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, MagnifyingGlassIcon, UserIcon, PhoneIcon } from '@heroicons/react/24/outline';

const SelectUserModal = ({ isOpen, onClose, users, onSelectUser }) => {
    const [searchQuery, setSearchQuery] = useState('');

    const filteredUsers = users.filter(user => {
        const query = searchQuery.toLowerCase();
        return (
            user.name?.toLowerCase().includes(query) ||
            user.number?.toLowerCase().includes(query) ||
            user.email?.toLowerCase().includes(query)
        );
    });

    const handleSelectUser = (user) => {
        onSelectUser(user);
        setSearchQuery('');
        onClose();
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div className='fixed inset-0 z-[9999] flex items-end md:items-center justify-center md:p-4'>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        onClick={onClose}
                        className='absolute inset-0 bg-black/60 backdrop-blur-sm'
                    />

                    {/* Modal */}
                    <motion.div
                        initial={{ y: '100%', opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: '100%', opacity: 0 }}
                        transition={{
                            type: 'spring',
                            damping: 30,
                            stiffness: 300,
                            mass: 0.8,
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className='relative w-full max-h-[90vh] md:max-h-[85vh] md:max-w-2xl bg-white rounded-t-3xl md:rounded-2xl shadow-2xl border-t md:border border-slate-200 flex flex-col'
                    >
                        {/* Drag Handle (Mobile Only) */}
                        <div className='md:hidden flex justify-center pt-3 pb-2'>
                            <div className='w-12 h-1.5 bg-slate-300 rounded-full' />
                        </div>

                        {/* Header */}
                        <div className='p-4 border-b border-slate-100 bg-slate-50'>
                            <div className='flex items-center justify-between'>
                                <div className='flex-1 pr-3'>
                                    <h3 className='text-[10px] font-black text-slate-400 uppercase tracking-widest'>
                                        Select User
                                    </h3>
                                    <h2 className='text-lg md:text-xl font-black text-slate-900 mt-1'>
                                        Choose a user to schedule appointment
                                    </h2>
                                </div>
                                <button
                                    onClick={onClose}
                                    type='button'
                                    className='p-2 rounded-lg hover:bg-slate-100 transition-colors flex-shrink-0'
                                >
                                    <XMarkIcon className='h-5 w-5 text-slate-500 hover:text-slate-900' />
                                </button>
                            </div>

                            {/* Search */}
                            <div className='mt-4 relative'>
                                <MagnifyingGlassIcon className='absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400' />
                                <input
                                    type='text'
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder='Search by name, mobile, or email...'
                                    className='w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500 transition-all font-medium'
                                    autoFocus
                                />
                            </div>
                        </div>

                        {/* User List - Scrollable */}
                        <div className='flex-1 overflow-y-auto p-4'>
                            {filteredUsers.length === 0 ? (
                                <div className='text-center py-12'>
                                    <UserIcon className='h-12 w-12 text-slate-300 mx-auto mb-3' />
                                    <p className='text-sm text-slate-500 font-medium'>
                                        {searchQuery ? 'No users found matching your search' : 'No users available'}
                                    </p>
                                </div>
                            ) : (
                                <div className='space-y-2'>
                                    {filteredUsers.map((user) => (
                                        <button
                                            key={user.id}
                                            onClick={() => handleSelectUser(user)}
                                            className='w-full p-4 bg-slate-50 hover:bg-blue-50 border border-slate-200 hover:border-blue-300 rounded-xl transition-all text-left group'
                                        >
                                            <div className='flex items-center gap-3'>
                                                <div className='w-10 h-10 bg-slate-200 group-hover:bg-blue-200 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors'>
                                                    <UserIcon className='h-5 w-5 text-slate-600 group-hover:text-blue-600' />
                                                </div>
                                                <div className='flex-1 min-w-0'>
                                                    <h4 className='font-black text-slate-900 group-hover:text-blue-900 truncate'>
                                                        {user.name}
                                                    </h4>
                                                    <div className='flex items-center gap-2 mt-1'>
                                                        <PhoneIcon className='h-3 w-3 text-slate-400' />
                                                        <span className='text-xs text-slate-600 font-medium'>
                                                            {user.number}
                                                        </span>
                                                    </div>
                                                    {user.leadType && (
                                                        <span
                                                            className={`inline-block mt-2 px-2 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-widest ${
                                                                user.leadType === 'business'
                                                                    ? 'bg-purple-100 text-purple-700 border border-purple-200'
                                                                    : 'bg-blue-100 text-blue-700 border border-blue-200'
                                                            }`}
                                                        >
                                                            {user.leadType === 'business' ? 'Business' : 'Service'}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className='sticky bottom-0 bg-white border-t border-slate-100 px-4 py-3 flex items-center justify-between'>
                            <p className='text-xs text-slate-500 font-medium'>
                                {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''} available
                            </p>
                            <button
                                type='button'
                                onClick={onClose}
                                className='px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-600 hover:bg-slate-100 transition-all'
                            >
                                Cancel
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default SelectUserModal;
